import React, { lazy, Suspense, useEffect, useRef } from "react"
import {
  useLocation,
  useNavigate,
  useParams,
  useRoutes,
} from "react-router-dom"
import { useDispatch } from "react-redux"
import { IntlProvider } from "react-intl"
import { ConfigureAxios } from "./axios.config"
import { routes } from "./routes/router"
import { useRNBSelector } from "./store/rootReducer"
import { getUserAllAccessThunk } from "./store/ducks/user.ducks"
import ErrorBoundary from "./errorboundary"
import { PageLoading } from "./components/PageLoading"
import { StyledToastContainer } from "./components/Toaster"
import { ZendeskCommand } from "./utils/zendesk"
import { defaultRichTextElements } from "./utils/intlConfig"
import { getIdFromParams } from "./utils/company"

declare global {
  interface Window {
    zE?: (...args: ZendeskCommand) => void
  }
}

interface ChildrenProp {
  children: React.ReactNode
}

export const CustomIntlProvider = lazy(async () => {
  const translations = await import("./i18n").then((m) => m.translations)

  return {
    default: ({ children }: ChildrenProp) => (
      <IntlProvider
        locale="fr"
        messages={translations}
        defaultRichTextElements={defaultRichTextElements}
      >
        {children}
      </IntlProvider>
    ),
  }
})

const UserPermissionRedirection = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const selectedCompanyId = getIdFromParams(useParams())("company_id") || 0

  const redirectionDirective = useRNBSelector((state) => {
    return state.user.redirection
  })
  const user = useRNBSelector((state) => {
    return state.user
  })
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const ZENDESK_SCRIPT_ID = "ze-snippet"

    const loadZendesk = () => {
      if (document.getElementById(ZENDESK_SCRIPT_ID)) return

      const script = document.createElement("script")
      script.id = ZENDESK_SCRIPT_ID
      script.src =
        "https://static.zdassets.com/ekr/snippet.js?key=87da3ba8-b8e8-4c1d-863a-533c7d5a6772"

      document.body.appendChild(script)
    }

    const toggleZendesk = (retry = 0) => {
      if (retry > 10) return

      if (typeof window.zE === "function") {
        window.zE("messenger", user.showZendeskMessenger ? "show" : "hide")
      } else {
        timeoutRef.current = setTimeout(() => {
          toggleZendesk(retry + 1)
        }, 200)
      }
    }

    loadZendesk()
    toggleZendesk()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [user.typology, user.status, user.showZendeskMessenger])

  useEffect(() => {
    dispatch(getUserAllAccessThunk())
  }, [dispatch, location, navigate])

  useEffect(() => {
    if (location.pathname.includes("/redirect_bank_mobile")) {
      const pathMatch = location.pathname.match(
        /\/company\/(\d+)\/bank\/accounts\/redirect_bank_mobile/
      )
      let finalCompanyId: string | number = selectedCompanyId
      if (pathMatch && pathMatch[1]) {
        finalCompanyId = pathMatch[1]
      }
      const url = `chaintrust://${finalCompanyId.toString()}/bank`
      window.location.href = url
      return
    }
    if (
      redirectionDirective.directive === "ACCESS_DENIED" &&
      location.pathname !== "/access_denied"
    ) {
      navigate("/access_denied")
    } else if (
      redirectionDirective.directive === "FIRST_CONNEXION" &&
      location.pathname !== "/user/incomplete_registration"
    ) {
      navigate("/user/incomplete_registration")
    } else if (
      (user.typology === "administrator" || user.typology === "manager") &&
      redirectionDirective.directive === "TERMS_OF_SALES_NOT_ACCEPTED" &&
      location.pathname !== "/user/accept_terms_and_conditions"
    ) {
      navigate("/user/accept_terms_and_conditions")
    } else if (
      redirectionDirective.directive === "TERMS_OF_SERVICE_NOT_ACCEPTED" &&
      location.pathname !== "/user/accept_terms_and_conditions"
    ) {
      navigate("/user/accept_terms_and_conditions")
    } else if (
      redirectionDirective.directive === "FIDUCIARY_NOT_CREATED" &&
      location.pathname !== "/fiduciary/register"
    ) {
      navigate("/fiduciary/register")
    }
  }, [navigate, location.pathname, redirectionDirective.directive])
  return <></>
}

function RNB() {
  ConfigureAxios()
  const element = useRoutes(routes)
  return (
    <ErrorBoundary>
      <Suspense fallback={<PageLoading />}>
        <CustomIntlProvider>
          {element}
          <StyledToastContainer data-cy="toast-error" hideProgressBar={true} />
          <UserPermissionRedirection />
        </CustomIntlProvider>
      </Suspense>
    </ErrorBoundary>
  )
}

export default RNB
